import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { PackBySlugOrGuid, PaginatedPackSamples } from 'apollo/pack/pack.gql';
import {
    Pack,
    Sample,
    Repack,
    PackBySlugOrGuidQuery,
    PackBySlugOrGuidQueryVariables,
    PaginatedPackSamplesQuery,
    PaginatedPackSamplesQueryVariables,
} from 'types/generated/graphql';
import { isSample } from 'types/typeGuards/isSample';

const usePackQuery = ({
    updatePreview,
    updateSamples,
}: {
    updatePreview: (entity: Pack | Repack) => void;
    updateSamples: ({
        items,
        total,
    }: {
        items: Sample[];
        total: number;
    }) => void;
}) => {
    const [getPack] = useLazyQuery<
        PackBySlugOrGuidQuery,
        PackBySlugOrGuidQueryVariables
    >(PackBySlugOrGuid, {
        onCompleted: (data) => {
            data.pack && updatePreview(data.pack);
        },
    });

    const [getPackSamples] = useLazyQuery<
        PaginatedPackSamplesQuery,
        PaginatedPackSamplesQueryVariables
    >(PaginatedPackSamples, {
        onCompleted: (data) => {
            const samples =
                data?.paginatedPackSamples?.data?.filter(isSample) || [];

            updateSamples({
                items: samples,
                total:
                    data?.paginatedPackSamples?.meta?.totalSampleRecords ?? 0,
            });
        },
    });

    const getPackPreview = useCallback(
        (guid: string) => {
            getPack({
                variables: { packSlugOrGuid: guid },
            });
            getPackSamples({
                variables: { packGuid: guid, take: 8 },
            });
        },
        [getPack, getPackSamples],
    );

    return getPackPreview;
};

export default usePackQuery;
